import React, { useMemo } from "react";
import {
  Compass,
  MapPin,
  ShieldCheck,
} from "lucide-react";

// Cargos que compõem a Diretoria da Administração Vigente
const DIRETORIA_ORDER = [
  { key: "veneravel", labels: ["Venerável Mestre", "Veneravel Mestre", "Venerável", "Veneravel"], display: "Venerável Mestre", tone: "text-white" },
  { key: "primeiro_vigilante", labels: ["1º Vigilante", "1o Vigilante", "Primeiro Vigilante"], display: "1º Vigilante", tone: "text-white" },
  { key: "segundo_vigilante", labels: ["2º Vigilante", "2o Vigilante", "Segundo Vigilante"], display: "2º Vigilante", tone: "text-white" },
  { key: "orador", labels: ["Orador"], display: "Orador", tone: "text-white" },
  { key: "secretario", labels: ["Secretário", "Secretario"], display: "Secretário", tone: "text-sky-300" },
  { key: "chanceler", labels: ["Chanceler", "Chancelaria"], display: "Chanceler", tone: "text-cyan-300" },
  { key: "tesoureiro", labels: ["Tesoureiro", "Tesouraria"], display: "Tesoureiro", tone: "text-emerald-400" },
  { key: "hospitaleiro", labels: ["Hospitaleiro"], display: "Hospitaleiro", tone: "text-purple-300" },
];

const normalize = (s = "") =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

const findMemberByOffice = (members, labels) => {
  const targets = labels.map(normalize);
  return (members || []).find((m) => {
    const off = normalize(m.office || "");
    return targets.some((t) => off === t || off.includes(t));
  });
};

import { formatLojaName } from "../utils/lodgeName";

export const LodgeProfileView = ({ members = [], currentLoja }) => {
  const displayName = currentLoja
    ? formatLojaName(currentLoja)
    : "Sua Loja Maçônica";

  const diretoria = useMemo(() => {
    return DIRETORIA_ORDER.map((role) => ({
      ...role,
      member: findMemberByOffice(members, role.labels),
    }));
  }, [members]);

  const address = currentLoja?.address?.trim() || "";
  const email = currentLoja?.email?.trim() || "";
  const phone = currentLoja?.phone?.trim() || "";
  const meetingSchedule = currentLoja?.meeting_schedule?.trim() || "";

  return (
    <div className="space-y-6" data-testid="lodge-profile-view">
      {/* Hero Card */}
      <div className="relative rounded-2xl overflow-hidden gold-border-glow bg-gradient-to-r from-[#111927] via-[#162238] to-[#0A0E17] p-6 sm:p-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center space-x-5">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#8A6715] flex items-center justify-center text-slate-950 p-1 shadow-[0_0_30px_rgba(212,175,55,0.4)] shrink-0">
              <div className="w-full h-full bg-[#080D18] rounded-xl flex items-center justify-center text-[#D4AF37] overflow-hidden">
                {currentLoja?.logo_b64 ? (
                  <img src={currentLoja.logo_b64} alt="Brasão" className="w-full h-full object-contain" />
                ) : (
                  <Compass className="w-10 h-10" />
                )}
              </div>
            </div>
            <div>
              <span className="text-xs font-bold text-[#D4AF37] uppercase tracking-widest block">
                Augusta e Respeitável Loja Simbólica
              </span>
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-white" data-testid="lodge-profile-name">
                {displayName}
              </h2>
              <p className="text-xs text-slate-300 mt-1">
                {currentLoja?.oriente ? `Oriente de ${currentLoja.oriente}` : ""}
                {currentLoja?.oriente && currentLoja?.obediencia ? " • " : ""}
                {currentLoja?.obediencia || ""}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2 text-xs font-mono bg-[#080D18] px-4 py-2.5 rounded-xl border border-[#D4AF37]/30">
            <span className="text-slate-400">Potência:</span>
            <span className="text-[#D4AF37] font-bold">{currentLoja?.obediencia || "—"}</span>
          </div>
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Templo & Reuniões */}
        <div className="luxury-card p-6 space-y-4" data-testid="lodge-templo-card">
          <h3 className="font-serif font-bold text-base text-white flex items-center space-x-2 border-b border-slate-800 pb-3">
            <MapPin className="w-4 h-4 text-[#D4AF37]" />
            <span>Templo & Sessões Litúrgicas</span>
          </h3>

          <div className="space-y-3 text-xs text-slate-300">
            <div>
              <span className="text-slate-400 block mb-0.5">Endereço do Templo Maçônico:</span>
              <p className="text-white font-medium" data-testid="lodge-address">
                {address || <span className="italic text-slate-500">Não cadastrado — edite em Super-Admin ou Perfil da Loja</span>}
              </p>
            </div>

            <div>
              <span className="text-slate-400 block mb-0.5">Dia e Horário dos Trabalhos:</span>
              <p className="text-[#D4AF37] font-semibold font-mono" data-testid="lodge-meeting-schedule">
                {meetingSchedule || <span className="italic text-slate-500 font-sans font-normal">A definir</span>}
              </p>
            </div>

            <div>
              <span className="text-slate-400 block mb-0.5">Contato da Secretaria / Hospedagem de Visitantes:</span>
              <p className="text-white font-mono" data-testid="lodge-contact">
                {phone || email ? (
                  <>
                    {phone}
                    {phone && email ? " • " : ""}
                    {email}
                  </>
                ) : (
                  <span className="italic text-slate-500 font-sans">Não cadastrado</span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Diretoria Atual (Luzes e Oficiais) */}
        <div className="luxury-card p-6 space-y-4" data-testid="lodge-diretoria-card">
          <h3 className="font-serif font-bold text-base text-white flex items-center space-x-2 border-b border-slate-800 pb-3">
            <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
            <span>Diretoria da Administração Vigente</span>
          </h3>

          <div className="space-y-2.5 text-xs">
            {diretoria.map((row) => (
              <div
                key={row.key}
                className="flex items-center justify-between p-2 rounded-lg bg-[#080D18]"
                data-testid={`diretoria-${row.key}`}
              >
                <span className="text-slate-400 font-semibold">{row.display}:</span>
                <span className={`${row.tone} font-bold text-right`}>
                  {row.member ? (
                    <>
                      {row.member.name}
                      {row.member.degree ? (
                        <span className="text-slate-400 font-normal ml-1">
                          ({row.member.degree.split(" - ")[1] || row.member.degree})
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <span className="italic text-slate-600 font-normal">Cargo vago</span>
                  )}
                </span>
              </div>
            ))}
          </div>

          <p className="text-[10px] text-slate-500 italic pt-2">
            Cargos são atualizados automaticamente a partir do campo "Cargo" de cada Irmão em Membros da Loja.
          </p>
        </div>
      </div>
    </div>
  );
};
